// Expiry settlement (ISS-029 / ISS-038 / ISS-039, 3 Nov 2026). Once the expiry session of an open SHADOW NFO index-option
// trade has ended its quotes are no longer executable: the trade shows EXPIRED · SETTLEMENT PENDING and nothing is sent.
// The preview lists every contract position (original + Profit-Lock / rescue legs, closed legs), the official settlement
// value and its source, the calculation, charges and the final P&L. Settling is a separate, explicit confirmation - an
// accounting event, never a broker order - and is refused while any blocker remains.
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

export const PENDING = "EXPIRED_SETTLEMENT_PENDING";
export const isSettlementPending = (row) => row?.expiry_lifecycle?.state === PENDING;

const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const signed = (v) => (v == null ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${inr(Math.abs(v))}`);
const tone = (v) => (v > 0 ? "text-[#008a04]" : v < 0 ? "text-[#FF3B30]" : "");
const apiErr = (e) => {
  const d = e?.response?.data?.detail;
  if (d?.blockers) return `Blocked: ${d.blockers.join("; ")}`;
  return typeof d === "string" ? d : e?.message || "Request failed";
};

export function ExpiredChip({ row, testId }) {
  if (!isSettlementPending(row)) return null;
  return (
    <span className="inline-block text-[9px] font-bold px-1 py-0.5 rounded-sm bg-amber-500/20 text-amber-700 dark:text-amber-400"
      title="The expiry session has ended: last-session prices are not executable; nothing is sent. Settle it from the settlement preview."
      data-testid={testId}>EXPIRED · SETTLEMENT PENDING</span>
  );
}

function PriceBlock({ rec }) {
  if (!rec) return null;
  const ok = ["VERIFIED", "CONFIRMED"].includes(rec.status);
  return (
    <div className="rounded-sm border border-[var(--term-border)] p-2 space-y-1" data-testid="xs-price">
      <div><span className="text-[var(--term-muted)]">Official settlement value </span>
        <b className="mono">{rec.price != null ? Number(rec.price).toFixed(2) : "not available"}</b>{" "}
        <span className={`font-bold ${ok ? "text-[#008a04]" : "text-amber-600"}`}>{rec.status}</span>
        {rec.index ? <span className="text-[var(--term-muted)]"> · {rec.index}</span> : null}</div>
      {rec.why && <div className="text-amber-700 dark:text-amber-400">{rec.why}</div>}
      {rec.reference && <div className="text-[var(--term-muted)]">Confirmed by {rec.confirmed_by} · {rec.reference}</div>}
      {(rec.readings || []).length > 0 && (
        <div className="text-[var(--term-muted)]">Readings: {rec.readings.map((r) => `${r.source} ${r.value ?? r.error}`).join(" · ")}</div>)}
      <div className="text-[10px] text-[var(--term-muted)]">{rec.convention || "NSE index options settle at the official closing value of the index on expiry day."}</div>
    </div>
  );
}

export default function ExpirySettlementDialog({ tradeId, onClose, onSettled }) {
  const [pv, setPv] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sure, setSure] = useState(false);
  const [price, setPrice] = useState("");
  const [ref, setRef] = useState("");

  const load = useCallback(async (refresh = false) => {
    setErr(null);
    try {
      const r = await api.get(`/options-hedge/trades/${tradeId}/expiry-settlement/preview`, { params: refresh ? { refresh_price: true } : {} });
      setPv(r.data);
    } catch (e) { setErr(apiErr(e)); }
  }, [tradeId]);
  useEffect(() => { load(); }, [load]);

  const confirmPrice = async () => {
    setBusy(true);
    try { await api.post(`/options-hedge/trades/${tradeId}/expiry-settlement/price`, { price: Number(price), reference: ref }); await load(); }
    catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };
  const settle = async () => {
    setBusy(true);
    try { const r = await api.post(`/options-hedge/trades/${tradeId}/expiry-settlement`, { confirm: true }); onSettled && onSettled(r.data); await load(); }
    catch (e) { setErr(apiErr(e)); } finally { setBusy(false); setSure(false); }
  };

  const c = pv?.calculation;
  const t = c?.totals || {};
  const priceOk = ["VERIFIED", "CONFIRMED"].includes(pv?.settlement_price_record?.status);
  const settled = pv?.event?.status === "APPLIED";
  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true"
      aria-label="Expiry settlement preview" data-testid="xs-dialog">
      <div className="term-panel-2 rounded-md w-full max-w-3xl max-h-[90vh] overflow-auto p-4 space-y-3 text-xs">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-bold">Expiry settlement {settled ? "— settled" : "preview"}</h2>
            <div className="text-[var(--term-muted)]">{pv ? `${pv.underlying} · expiry ${pv.expiry} · #${String(tradeId).slice(0, 8)} · ${pv.status}` : "Loading…"}</div>
          </div>
          <button type="button" className="px-2 py-1 border rounded-sm border-[var(--term-border)]" onClick={onClose} data-testid="xs-close">Close</button>
        </div>
        {err && <div className="text-[#FF3B30] border border-[#FF3B30]/40 rounded-sm px-2 py-1" data-testid="xs-error">{err}</div>}
        {pv && <>
          <PriceBlock rec={pv.settlement_price_record} />
          {!priceOk && !settled && (
            <div className="flex flex-wrap gap-2 items-end" data-testid="xs-price-form">
              <button type="button" className="px-2 py-1 border rounded-sm border-[var(--term-border)]" onClick={() => load(true)} data-testid="xs-refresh">Fetch official close again</button>
              <label className="flex flex-col">Confirm value<input className="term-input mono w-28" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="xs-price-input" /></label>
              <label className="flex flex-col grow">Reference (e.g. NSE bhavcopy 03-Nov-2026)<input className="term-input" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="xs-ref-input" /></label>
              <button type="button" disabled={busy || !price || !ref.trim()} className="px-2 py-1 rounded-sm bg-[#2563EB] text-white disabled:opacity-50" onClick={confirmPrice} data-testid="xs-price-confirm">Confirm value</button>
            </div>)}
          <table className="w-full" data-testid="xs-positions">
            <thead><tr className="text-left text-[var(--term-muted)]">
              <th>Contract</th><th className="text-right">Bought</th><th className="text-right">Sold</th><th className="text-right">Net</th>
              <th className="text-right">Premium cash</th><th className="text-right">Fill charges</th><th className="text-right">Intrinsic</th><th>State</th>
              <th className="text-right">Settlement</th><th className="text-right">Exercise STT</th></tr></thead>
            <tbody>{(c?.positions || []).map((r) => (
              <tr key={r.instrument_token} className="border-t border-[var(--term-border)]" data-testid={`xs-pos-${r.symbol}`}>
                <td className="mono">{r.symbol}{r.partial_orders ? <span className="text-amber-600" title="includes partially filled orders (filled quantity only)"> · partial</span> : null}</td>
                <td className="text-right mono">{r.bought}</td><td className="text-right mono">{r.sold}</td>
                <td className="text-right mono font-bold">{r.net_qty > 0 ? `+${r.net_qty}` : r.net_qty}</td>
                <td className={`text-right mono ${tone(r.premium_cash)}`}>{signed(r.premium_cash)}</td>
                <td className="text-right mono">{inr(r.fill_charges)}</td>
                <td className="text-right mono">{r.intrinsic != null ? r.intrinsic.toFixed(2) : "—"}</td>
                <td>{r.net_qty === 0 ? "closed" : r.moneyness || "—"}</td>
                <td className={`text-right mono ${tone(r.settlement_cash)}`}>{signed(r.settlement_cash)}</td>
                <td className="text-right mono">{inr(r.exercise_stt)}</td>
              </tr>))}</tbody>
          </table>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="xs-totals">
            <div>Premium received − paid <b className={`mono ${tone(t.premium_cash)}`}>{signed(t.premium_cash)}</b></div>
            <div>Settlement value <b className={`mono ${tone(t.settlement_cash)}`}>{signed(t.settlement_cash)}</b></div>
            <div>Gross <b className={`mono ${tone(t.gross)}`}>{signed(t.gross)}</b></div>
            <div>Fill charges <b className="mono">{inr(t.fill_charges)}</b></div>
            <div>Settlement charges <b className="mono">{inr(t.settlement_charges)}</b></div>
            <div className="text-sm">Final net P&L <b className={`mono ${tone(t.net)}`} data-testid="xs-net">{signed(t.net)}</b></div>
          </div>
          <div className="text-[10px] text-[var(--term-muted)]">Charges: {c?.charges_model}. Closed legs net to zero quantity: their premium and charges are counted once and add no settlement value. Settlement is an accounting event — no broker order is created.</div>
          {pv.blockers?.length > 0 && (
            <div className="rounded-sm border border-[#FF3B30]/50 p-2 text-[#FF3B30]" data-testid="xs-blockers">
              <b>Cannot settle yet:</b><ul className="list-disc pl-4">{pv.blockers.map((b) => <li key={b}>{b}</li>)}</ul></div>)}
          {!settled && (
            <div className="flex flex-wrap gap-2 justify-end items-center">
              {!sure ? (
                <button type="button" disabled={!pv.ready || busy} onClick={() => setSure(true)} data-testid="xs-settle"
                  className="px-3 py-1.5 rounded-sm bg-amber-600 text-white font-bold disabled:opacity-50">Settle at expiry…</button>
              ) : (<>
                <span>Book {signed(t.net)} as the final P&L and complete this trade?</span>
                <button type="button" onClick={() => setSure(false)} className="px-2 py-1 border rounded-sm border-[var(--term-border)]">Cancel</button>
                <button type="button" disabled={busy} onClick={settle} data-testid="xs-settle-yes" className="px-3 py-1.5 rounded-sm bg-amber-600 text-white font-bold">Yes, settle</button>
              </>)}
            </div>)}
        </>}
      </div>
    </div>
  );
}
